/**
 * „← Menü" aus den Einstellungen: wohin, und wo steht danach der Fokus?
 * (24.09.2026, Ticket 2570)
 *
 * Der Zurück-Pfeil der Einstellungen (und, wo er noch gebraucht wird, jedes
 * andere Verlassen) führt in den Bereich davor. Ohne Vorgänger (direkt in die Einstellungen geladen)
 * geht es ins Zuhause.
 *
 * Eigenes Modul, damit das VERHALTEN prüfbar ist — app.ts lässt sich im Test
 * nicht starten, und ein Test auf deren Quelltext prüfte nur die Form.
 */

/** Merkt sich die Ansicht VOR der jetzigen; `wechsel` ruft switchToTab. */
export interface Rueckweg {
  wechsel(bisher: string | undefined, ziel: string): void;
  /** Wohin „← Menü" jetzt führt. */
  ziel(): string;
}

/**
 * @param grundansichten die Ansichten, an die man zurückwill — app.ts gibt
 *   seine `ANSICHTEN` (dieselbe Liste wie fürs Neuladen, ohne „login").
 */
export function rueckwegAusEinstellungen(grundansichten: ReadonlySet<string>): Rueckweg {
  let vorige = "";
  return {
    wechsel(bisher, ziel) {
      // Nur Grundansichten — eine Anmeldemaske ist kein Ort, an den man zurückwill.
      if (bisher && bisher !== ziel && grundansichten.has(bisher)) vorige = bisher;
    },
    ziel() {
      return vorige && vorige !== "settings" ? vorige : "zuhause";
    },
  };
}

/**
 * „Menü" in der Fußzeile. (29.09.2026, Ticket 2803; umgebaut 28.10.2026)
 *
 * Martin: Die Fußzeile ist überall gleich, „Menü" öffnet IMMER die
 * Seitenleiste. Am Telefon klappt sie über die Ansicht. Am Rechner steht sie
 * ohnehin da (auch in den Einstellungen, als schmale Symbolleiste neben der
 * Liste) — dann geht der Fokus hinein, damit der Tipp sichtbar etwas tut und
 * die Tastatur gleich dort weitermacht. Vorher verließ „Menü" in den
 * Einstellungen am Rechner die Einstellungen.
 *
 * ⚠️ Gefragt wird der Browser, ob die Leiste zu sehen ist, nicht eine zweite
 *    Breitenangabe. Die Grenzen haben EINEN Besitzer: style.css.
 */
export function menueAusDerFusszeile(doc: Document, oeffneLeiste: () => void): void {
  oeffneLeiste();
  const leiste = doc.getElementById("nav-drawer");
  if (!leiste || leiste.getClientRects().length === 0) return;
  // Der erste Eintrag, der Fokus annimmt (Zuhause/Chat, sonst der Ein-/Ausklapp-Knopf).
  const kandidaten = [...leiste.querySelectorAll<HTMLElement>(".nav-ort-knopf:not(:disabled)"), ...leiste.querySelectorAll<HTMLElement>(".nav-drawer-toggle")];
  for (const k of kandidaten) {
    k.focus({ preventScroll: true });
    if (doc.activeElement === k) return;
  }
}

/**
 * Nach „← Menü" steht der Fokus im Menü, nicht auf <body>. (Prüfbefund
 * 24.09.: mit der Tastatur Enter auf „← Menü" — die Ansicht wechselte
 * richtig, `document.activeElement` war danach BODY; wer mit Tastatur oder
 * Bildschirmleser arbeitet, stand im Nichts.)
 *
 * Der Reihe nach, das erste, das den Fokus wirklich annimmt:
 *   1. der Einstellungen-Knopf der App-Leiste — dorther kam man;
 *   2. in der Symbolleiste (dort ist der Fuß ausgeblendet) der Eintrag des
 *      Bereichs, in dem man jetzt steht;
 *   3. sonst der Menü-Knopf oben in der Symbolleiste (Zuhause und Chat
 *      stehen dort nicht, gemessen bei 1024);
 *   4. die Überschrift der Zielansicht, sonst die Ansicht selbst.
 * „Wirklich annimmt" heißt: danach ist er `document.activeElement`. Ein
 * ausgeblendeter Knopf nimmt keinen Fokus an — so braucht es keine
 * eigene Sichtbarkeitsprüfung.
 */
export function fokusNachEinstellungen(doc: Document, ziel: string): void {
  const view = doc.getElementById(`view-${ziel}`);
  const kandidaten: (HTMLElement | null)[] = [
    doc.getElementById("open-settings-action"),
    doc.querySelector<HTMLElement>(`.nav-drawer .nav-item[data-tab="${ziel}"]`),
    doc.getElementById("nav-drawer-toggle"),
    view?.querySelector<HTMLElement>("h1, h2") ?? null,
    view,
  ];
  for (const k of kandidaten) {
    if (!k) continue;
    if (!k.matches("button, a[href], input, select, textarea, [tabindex]")) {
      k.setAttribute("tabindex", "-1");
    }
    k.focus({ preventScroll: true });
    if (doc.activeElement === k) return;
  }
}
